import {useState, useEffect, ChangeEvent, FormEvent} from 'react';
import {format, validate} from "rut.js";
import {useForm, SubmitHandler} from "react-hook-form";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import CONFIG from '../../../../../config';


interface AgendaStep6ExamenesProps {
  setStepValue: (value: number) => void;
  setCurrentStep: (step: string) => void;
  stepData: {
    StepFourthExamenes: {
      examenName: string;
      questions: string[];
    };
  };
}

interface FormData {
  name: string;
  lastName: string;
  rut: string;
  email: string;
  phone: string;
  action: string;
  typeExamen: string;
  questions: string[];
}

export default function AgendaStep6Examenes({ setStepValue, setCurrentStep, stepData }: AgendaStep6ExamenesProps) {
  const {
    register,
    handleSubmit,
    trigger,
    formState: {errors},
  } = useForm();

  const apiUrl = (CONFIG.APP.API_SCHEDULE_URL ? CONFIG.APP.API_SCHEDULE_URL : '')

  const [canContinue, setCanContinue] = useState(false);

  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [rut, setRut] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [isValidEmail, setIsValidEmail] = useState(false);
  const [messageRutError, setMessageRutError] = useState(false);
  const [messageEmailError, setMessageEmailError] = useState(false);

  const [isEmailBlurred, setIsEmailBlurred] = useState(false);
  const [isRutBlurred, setIsRutBlurred] = useState(false);
  const [captchaCode, setCaptchaCode] = useState(0);
  const [hasError, setHasError] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const changeRecaptcha = (value: any) => {
    setCaptchaCode(value);
    if (value !== 0) {
      setHasError(false);
    } else {
      setHasError(true);
    }
  }

  useEffect(() => {
    if (isEmailBlurred) {
      if (!isValidEmail) {
        setMessageEmailError(true);
      } else {
        setMessageEmailError(false);
      }
    }

    if (isRutBlurred) {
      let isValidRut = validate(format(rut)) === true;
      if (!isValidRut) {
        setMessageRutError(true);
      } else {
        setMessageRutError(false);
        setRut(format(rut));
      }
    }

    if (name && lastName && rut && email && phone && isValidEmail && !messageRutError && !messageEmailError) {
      setCanContinue(true);
    } else {
      setCanContinue(false);
    }
  }, [name, lastName, rut, email, phone, isValidEmail, isEmailBlurred, isRutBlurred]);

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(value));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>, setState: React.Dispatch<React.SetStateAction<string>>, validator?: (value: string) => void) => {
    const value = event.target.value;
    setState(value);
    if (validator) {
      validator(value);
    }
  };

  const handleBlur = (validator: React.Dispatch<React.SetStateAction<boolean>>) => {
    validator(true);
  };

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      setLoading(true);
      let stepFourthExamenes = stepData.StepFourthExamenes;
      if (typeof stepFourthExamenes === 'string') {
        stepFourthExamenes = JSON.parse(stepFourthExamenes);
      }

      data.action = 'contact_request';
      data.typeExamen = stepFourthExamenes?.examenName || '';
      data.questions = stepFourthExamenes?.questions || [];

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (key === 'questions') {
            // Procesar cada pregunta individualmente en el formato requerido
            value.forEach((item, index) => {
              Object.entries(item).forEach(([field, fieldValue]) => {
                formData.append(`${key}[${index}][${field}]`, fieldValue as string);
              });
            });
          } else {
            value.forEach((item, index) => {
              formData.append(`${key}[${index}]`, item);
            });
          }
        } else {
          formData.append(key, value as string);
        }
      });

      const response = await axios.post(
          `${apiUrl}/wp-json/agenda/v1/contact`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
      );

      if (response.status === 200) {
        console.log('Respuesta del servidor:', response.data);
        setCurrentStep('thank-you-page');
      } else {
        console.error('Error en la petición:', response.statusText);
      }

    } catch (error) {
      console.error('Error en la solicitud:', error);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleSubmitForm = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    await handleSubmit(onSubmit)();
  };

  return (
    <>
      <div>
        <div className="agenda-container">
          <div className="agenda-medico-bar mw-450 centro mt-40 mb-10 wmax-352 mx-auto">
            <div className="mx-auto w-48">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8 21H5C3.9 21 3 22 3 23.2V28.8C3 30 3.9 31 5 31H8V17.3C8 9.9 15.4 2 24.5 2C33.6 2 41 10 41 17.3V25"
                  stroke="#0098D8" strokeWidth="2" strokeMiterlimit="10"/>
                <path d="M41 21H43C44.1 21 45 22 45 23.2V28.8C45 30 44.1 31 43 31H41" stroke="#0098D8" strokeWidth="2"
                      strokeMiterlimit="10"/>
                <path d="M37 25V28C37 31.6 35.1 36.3 32 38.6" stroke="#0098D8" strokeWidth="2" strokeMiterlimit="10"/>
                <path d="M18 9C18 16 31.9 25 39 25" stroke="#0098D8" strokeWidth="2" strokeMiterlimit="10"/>
                <path d="M19 13C16 14.2 12 19.8 12 24V28C12 32.4 15.1 37.7 20.1 40" stroke="#0098D8" strokeWidth="2"
                      strokeMiterlimit="10"/>
                <path
                  d="M29 43C29 44.1 28.1 45 27 45H24C22.9 45 22 44.1 22 43C22 41.9 22.9 41 24 41H27C28.1 41 29 41.9 29 43Z"
                  stroke="#0098D8" strokeWidth="2" strokeMiterlimit="10"/>
                <path d="M28 43H29.2C35.7 43 41 37.7 41 31.2V28" stroke="#0098D8" strokeWidth="2" strokeMiterlimit="10"/>
              </svg>
            </div>

            <h4 className="text-center fw-700 fz-16 lh-24">
              No fue posible reservar tu hora, pero estamos aquí para ayudarte
            </h4>
            <p className="text-center fw-400 fz-13 lh-20 mb-20">
              Déjanos tus datos y te contactaremos lo antes posible para asistirte personalmente.
            </p>

            <form onSubmit={(e) => {
              handleSubmitForm(e)
            }}>
              <div className="d-block">
                <label className="fw-700 fz-13 lh-20 d-block">Nombre</label>
                <input
                  type="text"
                  className="input-style"
                  placeholder="Ingresa tu nombre"
                  value={name}
                  {...register("name", {required: 'Este campo es requerido'})}
                  onChange={(e) => handleInputChange(e, setName)}
                />
              </div>
              <div className="d-block">
                <label className="fw-700 fz-13 lh-20 d-block">Apellido</label>
                <input
                  type="text"
                  className="input-style"
                  placeholder="Ingresa tu apellido"
                  value={lastName}
                  {...register("lastName", {required: 'Este campo es requerido'})}
                  onChange={(e) => handleInputChange(e, setLastName)}
                />
              </div>
              <div className="d-block">
                <label className="fw-700 fz-13 lh-20 d-block">Rut/Pasaporte</label>
                <input
                  type="text"
                  className="input-style"
                  placeholder="ej: 00000000-0"
                  value={rut}
                  {...register("rut", {required: 'Este campo es requerido'})}
                  onChange={(e) => handleInputChange(e, setRut)}
                />
                {messageRutError && isRutBlurred && (
                  <span className="error">Registre un rut valido</span>
                )}
              </div>
              <div className="d-block">
                <label className="fw-700 fz-13 lh-20 d-block">Email</label>
                <input
                  type="email"
                  className="input-style"
                  placeholder="ejemplo@email.com"
                  value={email}
                  {...register("email", {required: 'Este campo es requerido'})}
                  onChange={(e) => handleInputChange(e, setEmail, validateEmail)}
                />
                {!isValidEmail && (
                  <span className="error">Registre un correo electronico valido</span>
                )}
              </div>
              <div className="d-block">
                <label className="fw-700 fz-13 lh-20 d-block">Celular</label>
                <input
                  type="phone"
                  className="input-style"
                  placeholder="+56 9 9999 9999"
                  value={phone}
                  maxLength={9}
                  {...register("phone", {required: 'Este campo es requerido'})}
                  onChange={(e) => handleInputChange(e, setPhone)}
                  pattern="[0-9]*"
                  onKeyDown={(e) => {
                    if (!/[0-9]/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              <div className="d-flex flex-center mt-10">
                <ReCAPTCHA sitekey="6LdtXiImAAAAAPCc7CmLqRi3cqtqugfTT77fLZUG" onChange={changeRecaptcha}/>
              </div>
              <input
                type="submit"
                className={`${canContinue && !hasError && !loading ? 'button-blue mt-20 mx-auto' : 'button-blue mt-20 mx-auto disabled'} mt-20 mx-auto`}
                value={loading ? 'Enviando...' : 'SOLICITAR CONTACTO'}
                disabled={loading || !canContinue || hasError}
              />
             {/* <input
                type="submit"
                className={`${canContinue && !hasError ? '' : 'disabled'} button-blue mt-20 mx-auto`}
                value="SOLICITAR CONTACTO"
              />*/}
            </form>
            <p className="text-center fw-400 fz-12 lh-16 text-gray">
              Los datos solicitados son necesarios para gestionar que nuestros ejecutivos puedan contactarte.
            </p>
          </div>
        </div>
      </div>
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </>
  );
}